import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, User, Loader2, ArrowLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    enqueueManualNotification,
    getManagedUserProfile,
} from "@/lib/api";
import { getErrorMessage } from "@/lib/error-messages";

const directMessageSchema = z.object({
    subject: z.string().min(1, "Betreff ist erforderlich"),
    body: z.string().min(1, "Text ist erforderlich"),
});

type DirectMessageValues = z.infer<typeof directMessageSchema>;

interface DirectMessagePageProps {
    userId: string;
    onBack?: () => void;
}

export function DirectMessagePage({ userId, onBack }: DirectMessagePageProps) {
    const navigate = useNavigate();
    const { data: profile, isLoading: isLoadingProfile } = useQuery({
        queryKey: ["managed-user-profile", userId],
        queryFn: () => getManagedUserProfile(userId),
    });

    const form = useForm<DirectMessageValues>({
        resolver: zodResolver(directMessageSchema),
        defaultValues: {
            subject: "",
            body: "",
        },
    });

    const sendMutation = useMutation({
        mutationFn: (values: DirectMessageValues) =>
            enqueueManualNotification({
                templateSlug: "manual",
                userIds: [userId],
                customSubject: values.subject,
                customBody: values.body,
                isBulk: false,
            }),
        onSuccess: () => {
            toast.success("Nachricht wurde versendet.");
            if (onBack) onBack();
            else navigate(-1);
        },
        onError: (error) => toast.error(getErrorMessage(error)),
    });

    if (isLoadingProfile) {
        return (
            <div className="flex items-center justify-center p-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => onBack ? onBack() : navigate(-1)}>
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Nachricht senden</h1>
                    <p className="text-muted-foreground">
                        Direkte Nachricht an den Benutzer.
                    </p>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <User className="h-5 w-5 text-primary" />
                        Empfänger: {profile?.first_name} {profile?.last_name}
                    </CardTitle>
                    <CardDescription>
                        Die Nachricht wird per E-Mail an den Benutzer versendet.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(v => sendMutation.mutate(v))} className="space-y-6">
                            <FormField
                                control={form.control}
                                name="subject"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Betreff</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Betreff der Nachricht" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="body"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Text</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder="Deine Nachricht an den Benutzer..."
                                                className="min-h-[200px]"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <Button type="submit" className="w-full" disabled={sendMutation.isPending}>
                                {sendMutation.isPending ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sende...</>
                                ) : (
                                    <><Send className="mr-2 h-4 w-4" /> Nachricht versenden</>
                                )}
                            </Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
}
